import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableFooter from "@mui/material/TableFooter";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import { visuallyHidden } from "@mui/utils";
import { useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import TextField from "@mui/material/TextField";
import ClearIcon from "@mui/icons-material/Clear";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import HistoryDialog from "./HistoryDialog";
import ConfigData from "../config/config.json";
import { translateExaminationEnum } from "../pharmacies/HistoryTable";
import Switch from "@mui/material/Switch";
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useTheme } from "@mui/material";

const webserver = ConfigData.WEBSERVER;

const collator = new Intl.Collator("de", { sensitivity: "base" });

const SearchBar = ({ searchQuery, setSearchQuery }) => (
  <Box sx={{ display: "flex" }} mb={2}>
    <TextField
      id="search-bar"
      className="text"
      value={searchQuery}
      onInput={(e) => {
        setSearchQuery(e.target.value);
      }}
      variant="outlined"
      placeholder="Suchen ..."
      size="small"
      sx={{ width: "100%" }}
      InputProps={{
        endAdornment: (
          <IconButton
            aria-label="search"
            onClick={(e) => {
              setSearchQuery("");
              document.getElementById("search-bar").focus();
            }}
          >
            {searchQuery ? <ClearIcon /> : <SearchIcon />}
          </IconButton>
        ),
      }}
    />
  </Box>
);

function descendingComparator(a, b, orderBy) {
  if (
    orderBy === "name" ||
    orderBy === "streetAddress" ||
    orderBy === "city" ||
    orderBy === "leader" ||
    orderBy === "governor"
  ) {
    //return a[orderBy].localeCompare(b[orderBy], 'de', {sensitivity: 'base'})
    return collator.compare(a[orderBy], b[orderBy]);
  }

  if (
    orderBy === "openingDate" ||
    orderBy === "closingDate" ||
    orderBy === "latestExaminationDate"
  ) {
    if (a[orderBy] === null && b[orderBy] !== null) {
      return 1;
    } else if (a[orderBy] !== null && b[orderBy] === null) {
      return -1;
    } else if (a[orderBy] === null && b[orderBy] === null) {
      return 0;
    } else {
      if (b[orderBy].substring(6) < a[orderBy].substring(6)) {
        return -1;
      } else if (b[orderBy].substring(6) > a[orderBy].substring(6)) {
        return 1;
      } else {
        if (b[orderBy].substring(3, 6) < a[orderBy].substring(3, 6)) {
          return -1;
        } else if (b[orderBy].substring(3, 6) > a[orderBy].substring(3, 6)) {
          return 1;
        } else {
          if (b[orderBy].substring(0, 3) < a[orderBy].substring(0, 3)) {
            return -1;
          } else if (b[orderBy].substring(0, 3) > a[orderBy].substring(0, 3)) {
            return 1;
          } else {
            return 0;
          }
        }
      }
    }
  }

  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "id",
    numeric: false,
    disablePadding: false,
    label: "Apo. Nr.",
  },
  {
    id: "name",
    numeric: false,
    disablePadding: false,
    label: "Name",
  },
  {
    id: "streetAddress",
    numeric: false,
    disablePadding: false,
    label: "Straße & Hausnr.",
  },
  {
    id: "postalCode",
    numeric: false,
    disablePadding: false,
    label: "PLZ",
  },
  {
    id: "city",
    numeric: false,
    disablePadding: false,
    label: "Ort",
  },
  {
    id: "openingDate",
    numeric: false,
    disablePadding: false,
    label: "Öffnung",
  },
  {
    id: "closingDate",
    numeric: false,
    disablePadding: false,
    label: "Schließung",
  },
  {
    id: "phone",
    numeric: false,
    disablePadding: false,
    label: "Telefon",
  },
  {
    id: "leader",
    numeric: false,
    disablePadding: false,
    label: "Leiter",
  },
  {
    id: "governor",
    numeric: false,
    disablePadding: false,
    label: "Verantwortliche",
  },
  {
    id: "state",
    numeric: false,
    disablePadding: false,
    label: "Status",
  },
  {
    id: "latestExaminationDate",
    numeric: false,
    disablePadding: false,
    label: "Letztes Prüfdatum",
  },
  {
    id: "history",
    numeric: false,
    disablePadding: false,
    label: "Historie & Dokumenation",
  },
];

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
};

export default function Pharmacies() {
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("id");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [searchQuery, setSearchQuery] = useState("");
  const [showClosed, setShowClosed] = React.useState(false);
  const [rowss, setRowss] = useState([]);
  const theme = useTheme();

  React.useEffect(() => {
    fetch(
      webserver.protocol +
        "://" +
        webserver.host +
        ":" +
        webserver.port +
        "/pharmacy/getAll",
      { method: "GET", credentials: "include" }
    )
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Error: " + response.status);
        }
        return response;
      })
      .then((response) => response.json())
      .then((data) => {
        for (var i = 0; i < data.length; i++) {
          data[i].historyOpen = false;

          /*switch (data[i].state) {
            case 'NOT_EXAMINED':
              data[i].state = 'Ungeprüft';
              break;
            case 'PASSED':
              data[i].state = 'Bestanden';
              break;
            case 'FAILED':
              data[i].state = 'Durchgefallen';
              break;
            case 'MULTIPLE_FAILURES':
              data[i].state = 'Wiederholt durchgefallen';
              break;
            case 'PASSED_AFTER_FAILURE':
              data[i].state = 'Wiederholung bestanden';
              break;
          }*/
        }

        setRowss(data);
      })
      .catch((error) => {});
  }, []);

  function pharmacyIsActive(pharmacy) {
    let openingDate = "";
    //check if opening date is set
    if (pharmacy.openingDate != "") {
      openingDate = new Date(
        pharmacy.openingDate.substring(6, 10),
        parseInt(pharmacy.openingDate.substring(3, 5)) - 1,
        pharmacy.openingDate.substring(0, 2)
      );
    } else {
      console.log(pharmacy.name + ": opening date is not set!");
      return false;
    }

    let closingDate = "";
    //check if closing date is set
    if (pharmacy.closingDate != "") {
      closingDate = new Date(
        pharmacy.closingDate.substring(6, 10),
        parseInt(pharmacy.closingDate.substring(3, 5)) - 1,
        pharmacy.closingDate.substring(0, 2)
      );
    }

    //pharmacy not yet opened
    if (openingDate > new Date()) {
      return false;
    }
    //pharmacy opened and closing date is not set
    else if (openingDate <= new Date() && closingDate == "") {
      return true;
    }
    //pharmacy opened and not yet closed
    else if (openingDate <= new Date() && new Date() < closingDate) {
      return true;
    }
    //pharmacy already closed
    else {
      return false;
    }
  }

  function queryStatus(state) {
    switch (state) {
      case "ASSIGNED_TO_PROCESS":
        return "prozess zugewiesen".includes(searchQuery.toLowerCase());
      case "IN_PROGRESS":
        return "in Bearbeitung".includes(searchQuery.toLowerCase());
      case "NOT_EXAMINED":
        return "ungeprüft".includes(searchQuery.toLowerCase());
      case "PASSED":
        return "bestanden".includes(searchQuery.toLowerCase());
      case "FAILED":
        return "durchgefallen".includes(searchQuery.toLowerCase());
      case "MULTIPLE_FAILURES":
        return "wiederholt durchgefallen".includes(searchQuery.toLowerCase());
      case "PASSED_AFTER_FAILURE":
        return "wiederholung bestanden".includes(searchQuery.toLowerCase());
      default:
        return false;
    }
  }

  const filteredRows = rowss.filter((row) => {
    if (pharmacyIsActive(row)) {
      return (
        row.id.toString().toLowerCase().includes(searchQuery.toLowerCase()) ||
        (row.name !== null &&
          row.name
            .toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase())) ||
        (row.streetAddress !== null &&
          row.streetAddress
            .toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase())) ||
        (row.postalCode !== null &&
          row.postalCode
            .toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase())) ||
        (row.city !== null &&
          row.city
            .toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase())) ||
        (row.openingDate !== null &&
          row.openingDate
            .toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase())) ||
        (row.closingDate !== null &&
          row.closingDate
            .toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase())) ||
        (row.phone !== null &&
          row.phone
            .toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase())) ||
        (row.leader !== null &&
          row.leader
            .toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase())) ||
        (row.governor !== null &&
          row.governor
            .toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase())) ||
        (row.state !== null && queryStatus(row.state)) ||
        (row.latestExaminationDate !== null &&
          row.latestExaminationDate
            .toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase()))
      );
    } else if (showClosed) {
      return (
        row.id.toString().toLowerCase().includes(searchQuery.toLowerCase()) ||
        (row.name !== null &&
          row.name
            .toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase())) ||
        (row.streetAddress !== null &&
          row.streetAddress
            .toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase())) ||
        (row.postalCode !== null &&
          row.postalCode
            .toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase())) ||
        (row.city !== null &&
          row.city
            .toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase())) ||
        (row.openingDate !== null &&
          row.openingDate
            .toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase())) ||
        (row.closingDate !== null &&
          row.closingDate
            .toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase())) ||
        (row.phone !== null &&
          row.phone
            .toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase())) ||
        (row.leader !== null &&
          row.leader
            .toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase())) ||
        (row.governor !== null &&
          row.governor
            .toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase())) ||
        (row.state !== null && queryStatus(row.state)) ||
        (row.latestExaminationDate !== null &&
          row.latestExaminationDate
            .toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase()))
      );
    }
  });

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredRows.length) : 0;

  return (
    <Box sx={{ width: "100%" }}>
      <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
      <Paper sx={{ width: "100%", mb: 2 }}>
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={"medium"}
          >
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              setRowss={setRowss}
              rowss={rowss}
            />
            <TableBody>
              {/* if you don't need to support IE11, you can replace the `stableSort` call with:
                 rows.slice().sort(getComparator(order, orderBy)) */}
              {stableSort(filteredRows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, pageIndex) => {
                  let index = 0;

                  for (let found = 0; found < rowss.length; found++) {
                    if (rowss[found].id === row.id) {
                      index = found;
                      break;
                    }
                  }
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow hover tabIndex={-1} key={row.id}>
                      <TableCell
                        id={labelId}
                        scope="row"
                        sx={{ minWidth: 110 }}
                      >
                        {row.id}
                      </TableCell>
                      <TableCell align="left">
                        <Tooltip title={row.name} enterDelay={1000}>
                          <Typography
                            sx={{
                              minWidth: 200,
                              maxWidth: 200,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {row.name}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="left">
                        <Tooltip title={row.streetAddress} enterDelay={1000}>
                          <Typography
                            sx={{
                              minWidth: 200,
                              maxWidth: 200,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {row.streetAddress}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="left">{row.postalCode}</TableCell>
                      <TableCell align="left">
                        <Tooltip title={row.city} enterDelay={1000}>
                          <Typography
                            sx={{
                              minWidth: 100,
                              maxWidth: 100,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {row.city}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="left">{row.openingDate}</TableCell>
                      <TableCell align="left">{row.closingDate}</TableCell>
                      <TableCell align="left">
                        <Tooltip title={row.phone} enterDelay={1000}>
                          <Typography
                            sx={{
                              minWidth: 100,
                              maxWidth: 100,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {row.phone}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="left">
                        <Tooltip title={row.leader} enterDelay={1000}>
                          <Typography
                            sx={{
                              minWidth: 150,
                              maxWidth: 150,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {row.leader}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="left">
                        <Tooltip title={row.governor} enterDelay={1000}>
                          <Typography
                            sx={{
                              minWidth: 150,
                              maxWidth: 150,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {row.governor}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      {
                        //<TableCell align="left"><StatusSelect id={row.id} index={index} state={row.state} setRows={setRowss}/></TableCell>
                      }
                      <TableCell align="left">
                        <Tooltip
                          title={translateExaminationEnum(row.state)}
                          enterDelay={1000}
                        >
                          <Typography
                            sx={{
                              minWidth: 150,
                              maxWidth: 150,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {translateExaminationEnum(row.state)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="left">
                        {row.latestExaminationDate}
                      </TableCell>

                      <TableCell align="center">
                          <Tooltip title={"Historie anzeigen"} enterDelay={1000}>
                            <IconButton
                              onClick={() =>
                                setRowss((prev) => {
                                  let temp = JSON.parse(JSON.stringify(prev));
                                  temp[index].historyOpen = true;
                                  return temp;
                                })
                              }
                            >
                              <HistoryEduIcon sx={{}} />
                            </IconButton>
                          </Tooltip>
                      </TableCell>
                      <HistoryDialog
                        function={() => {}}
                        open={row.historyOpen}
                        setOpen={() =>
                          setRowss((prev) => {
                            let temp = JSON.parse(JSON.stringify(prev));
                            temp[index].historyOpen = false;
                            return temp;
                          })
                        }
                        name={row.name}
                        id={row.id}
                      />
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 73 * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan="3">
                  <FormControl>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={showClosed}
                            onChange={(event) =>
                              setShowClosed(event.target.checked)
                            }
                          />
                        }
                        label={
                          <Typography
                            style={{
                              color:
                                theme.palette.mode === "dark"
                                  ? "#fff"
                                  : "#000000DE",
                              fontSize: "14px",
                            }}
                          >
                            Geschlossene Apotheken anzeigen
                          </Typography>
                        }
                      />
                    </FormGroup>
                  </FormControl>
                </TableCell>
                <TableCell colSpan="100">
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredRows.length}
                    //count={rowss.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelDisplayedRows={({ from, to, count }) => {
                      if (from > count) {
                        setPage(0);
                      }
                      return "" + from + "-" + to + " von " + count;
                    }}
                    labelRowsPerPage="Zeilen pro Seite"
                  />
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
